import cx from "classnames";
import { ReactNode, useEffect, useRef } from "react";

type SuggestionListboxProps = {
  ariaLabel: string;
  className?: string;
  children: ReactNode;
};

type SuggestionOptionProps = {
  label: string;
  isHighlighted: boolean;
  onSelect: () => void;
  onHighlight: () => void;
  children: ReactNode;
};

export const SuggestionListbox = ({
  ariaLabel,
  className,
  children,
}: SuggestionListboxProps) => (
  <div
    role="listbox"
    aria-label={ariaLabel}
    className={cx(
      "t-absolute t-bottom-full t-left-0 t-z-popover t-mb-2 t-flex t-max-h-96 t-flex-col t-overflow-y-auto t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-white t-p-1 t-shadow-md sm:t-w-full",
      className
    )}
  >
    {children}
  </div>
);

export const SuggestionOption = ({
  label,
  isHighlighted,
  onSelect,
  onHighlight,
  children,
}: SuggestionOptionProps) => {
  const optionRef = useRef<HTMLDivElement>(null);

  // Keep the keyboard-highlighted row visible while arrowing through a long list
  useEffect(() => {
    if (isHighlighted)
      optionRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [isHighlighted]);

  // mousedown (not click) so the editor keeps focus and its selection
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    onSelect();
  };

  return (
    <div
      ref={optionRef}
      role="option"
      aria-label={label}
      aria-selected={isHighlighted}
      onMouseDown={handleMouseDown}
      // mousemove, not mouseenter: rows the arrow keys scroll under a still
      // cursor must not steal the highlight
      onMouseMove={onHighlight}
      className={cx(
        "t-flex t-cursor-pointer t-items-center t-gap-2 t-rounded t-px-2 t-py-1.5 t-text-body-sm t-text-text-100",
        { "t-bg-neutral-0": isHighlighted }
      )}
    >
      {children}
    </div>
  );
};
